/*
 * Sprint V102.ab (02.10.2026) — Admin QuickCheck-Fragenset A/B.
 *
 * Route: /admin/questionset-ab (nur mit Admin-Login sichtbar).
 * Zeigt v1 vs. v2 Opt-in-Rate, Primary-Type-Distribution und
 * durchschnittlichen Score-Lead (Eindeutigkeit der Zuordnung).
 */
import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, Trophy, TrendingUp, Users } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { TestExcludedNote } from "@/components/TestExcludedNote";

function fmtPercent(x) {
  if (typeof x !== "number") return "—";
  return `${(x * 100).toFixed(2)}%`;
}

const TYPES = ["eule", "biene", "katze", "hund"];
const TYPE_LABEL = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };

export default function AdminQuestionSetAB() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [days, setDays] = useState(14);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/questionset-ab?days=${days}`);
      setData(r.data);
      setLastUpdate(new Date());
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setError("Diese Seite ist nur für Admins. Bitte als Admin einloggen.");
      } else {
        setError("A/B-Daten konnten nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const sets = data?.sets || {};
  const v1 = sets.v1 || {};
  const v2 = sets.v2 || {};

  const winner = useMemo(() => {
    const v1Rate = v1.optin_rate || 0;
    const v2Rate = v2.optin_rate || 0;
    if ((v1.completions || 0) < 20 || (v2.completions || 0) < 20) return null;
    return v2Rate > v1Rate ? "v2" : v1Rate > v2Rate ? "v1" : null;
  }, [v1, v2]);

  const delta = data?.optin_rate_delta_v2_minus_v1 ?? 0;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-questionset-ab">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white">
              Fragenset A/B
            </h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              v1 (Likert + Kontext-Switcher) &nbsp;vs.&nbsp; v2 (Antreiber-Fokus,
              Direktstart, Forced-Choice-Tie-Breaker).
            </p>
            <TestExcludedNote count={data?.test_excluded} testId="qs-ab-test-excluded" />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="qs-ab-days"
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
            >
              {[7, 14, 30, 60].map((d) => (
                <option key={d} value={d}>
                  {d} Tage
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="qs-ab-refresh"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:border-slate-400 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Aktualisieren
            </button>
          </div>
        </div>

        {error && (
          <div
            className="rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-950/20 dark:border-rose-900 p-4 text-rose-800 dark:text-rose-300 text-sm"
            data-testid="qs-ab-error"
          >
            {error}
          </div>
        )}

        {!error && data && (
          <>
            {/* Delta-Banner */}
            <div
              className="mb-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 p-5"
              data-testid="qs-ab-delta"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    Opt-in-Rate · Delta v2 − v1
                  </p>
                  <p
                    className={`mt-2 text-3xl sm:text-4xl font-extrabold ${
                      delta > 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : delta < 0
                          ? "text-rose-600 dark:text-rose-400"
                          : "text-slate-500"
                    }`}
                  >
                    {delta > 0 ? "+" : ""}
                    {fmtPercent(delta)}
                  </p>
                </div>
                {winner && (
                  <div
                    className="inline-flex items-center gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-2 text-amber-900 dark:text-amber-200 font-bold text-sm"
                    data-testid="qs-ab-winner"
                  >
                    <Trophy className="w-4 h-4" />
                    Winner: {winner.toUpperCase()}
                  </div>
                )}
              </div>
              {!winner && (
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Noch zu wenige Daten für einen Winner (benötige ≥20 Completions pro Set).
                </p>
              )}
            </div>

            {/* Beide Sets Seite an Seite */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {["v1", "v2"].map((set) => {
                const s = sets[set] || {};
                return (
                  <div
                    key={set}
                    className={`rounded-2xl border bg-white dark:bg-slate-900 p-6 ${
                      winner === set
                        ? "border-emerald-400 shadow-emerald-200/50 shadow-lg dark:border-emerald-700"
                        : "border-slate-200 dark:border-slate-700"
                    }`}
                    data-testid={`qs-ab-${set}-card`}
                  >
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                        Set {set.toUpperCase()}
                      </h2>
                      <span className="text-xs text-slate-500 font-medium">
                        {set === "v1" ? "Likert + Kontext" : "Antreiber + Forced-Choice"}
                      </span>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3">
                        <p className="text-xs text-slate-500 font-semibold">Completions</p>
                        <p
                          className="mt-1 text-2xl font-extrabold text-slate-900 dark:text-white"
                          data-testid={`qs-ab-${set}-completions`}
                        >
                          {s.completions || 0}
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3">
                        <p className="text-xs text-slate-500 font-semibold">Leads</p>
                        <p
                          className="mt-1 text-2xl font-extrabold text-slate-900 dark:text-white"
                          data-testid={`qs-ab-${set}-leads`}
                        >
                          {s.leads || 0}
                        </p>
                      </div>
                      <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3">
                        <p className="text-xs text-emerald-700 dark:text-emerald-400 font-semibold">
                          Opt-in-Rate
                        </p>
                        <p
                          className="mt-1 text-2xl font-extrabold text-emerald-700 dark:text-emerald-300"
                          data-testid={`qs-ab-${set}-optin-rate`}
                        >
                          {fmtPercent(s.optin_rate)}
                        </p>
                      </div>
                    </div>

                    {/* Primary-Type-Distribution */}
                    <div className="mt-6">
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" />
                        Fokustyp-Verteilung
                      </p>
                      <div className="space-y-1.5">
                        {TYPES.map((t) => {
                          const n = s.primary_type_distribution?.[t] || 0;
                          const pct = s.completions
                            ? (n / s.completions) * 100
                            : 0;
                          return (
                            <div key={t} className="flex items-center gap-2 text-xs">
                              <span className="w-14 text-slate-600 dark:text-slate-300 font-semibold">
                                {TYPE_LABEL[t]}
                              </span>
                              <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                <div
                                  className="h-full bg-[#4563BB] transition-all duration-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="w-16 text-right text-slate-500 font-mono tabular-nums">
                                {n} · {pct.toFixed(0)}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Score-Lead (= Eindeutigkeit) */}
                    <div className="mt-6 flex items-center gap-2 text-sm">
                      <TrendingUp className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">⌀ Score-Lead:</span>
                      <span
                        className="font-bold text-slate-900 dark:text-white"
                        data-testid={`qs-ab-${set}-score-lead`}
                      >
                        {s.average_score_lead || 0}
                      </span>
                      <span className="text-xs text-slate-400">
                        (höher = eindeutigere Zuordnung)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {lastUpdate && (
              <p className="mt-6 text-xs text-slate-400 text-center">
                Letzter Datenstand: {lastUpdate.toLocaleTimeString("de-DE")} · Auto-Refresh alle 60 s
              </p>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
